/* ---- <product-sticky-bar> ---- */
(() => {
  'use strict';
  if (customElements.get('product-sticky-bar')) return;

  const strings = () => window.theme?.strings || {};
  const json = (el) => { try { return JSON.parse(el?.textContent || '[]'); } catch (e) { return []; } };

  class ProductStickyBar extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;

      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.productUrl;
      this.view = this.dataset.view ? `&view=${encodeURIComponent(this.dataset.view)}` : '';
      this.variants = json(this.querySelector('[data-sticky-variants]'));
      this.form = this.querySelector('form');
      this.idInput = this.form?.querySelector('[data-variant-id]');
      this.button = this.form?.querySelector('[data-add-button]');
      this.selects = [...this.querySelectorAll('select[data-sticky-option]')];
      this.variantId = this.idInput ? Number(this.idInput.value) : null;

      this.main = [...document.querySelectorAll('product-info')]
        .find((el) => el.dataset.productId === this.dataset.productId && !el.closest('ft-quickview'));

      this.addEventListener('change', (e) => {
        if (e.target.closest('select[data-sticky-option]')) this.onOptionChange();
      });
      this.onVariant = (e) => {
        if (!this.main || e.target !== this.main || !e.detail?.variant) return;
        this.sync(e.detail.variant);
      };
      document.addEventListener('variant:change', this.onVariant);

      this.resizer = new ResizeObserver(() => this.measure()); // its first callback measures, after layout
      this.resizer.observe(this);
      this.watch();

      if (this.hasAttribute('data-design-mode')) {
        this.onSelect = (e) => { if (e.detail?.sectionId === this.sectionId) { this.forced = true; this.toggle(true); } };
        this.onDeselect = (e) => { if (e.detail?.sectionId === this.sectionId) { this.forced = false; this.toggle(this.past); } };
        document.addEventListener('shopify:section:select', this.onSelect);
        document.addEventListener('shopify:section:deselect', this.onDeselect);
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizer?.disconnect();
      this.controller?.abort();
      document.removeEventListener('variant:change', this.onVariant);
      if (this.onSelect) {
        document.removeEventListener('shopify:section:select', this.onSelect);
        document.removeEventListener('shopify:section:deselect', this.onDeselect);
      }
      document.documentElement.style.removeProperty('--sticky-bar-height');
      this.bound = false;
    }

    /* ---- Showing ---- */

    watch() {
      const mainId = this.main?.dataset.sectionId;
      const target = this.main?.querySelector('[data-buy-buttons]')
        || (mainId && document.getElementById(`ProductForm-${mainId}`))
        || this.main;
      if (!target) { this.past = true; this.toggle(true); return; }
      this.observer = new IntersectionObserver(([entry]) => {
        this.past = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.toggle(this.past || this.forced);
      });
      this.observer.observe(target);
    }

    toggle(show) {
      this.classList.toggle('is-visible', !!show);
      this.setAttribute('aria-hidden', show ? 'false' : 'true');
      this.inert = !show;
      if (!show) this.querySelectorAll('custom-select.is-open').forEach((s) => s.hide?.());
    }

    measure() {
      const h = this.offsetHeight;
      if (h === this.measured) return; // a root custom property change restyles the whole page
      this.measured = h;
      document.documentElement.style.setProperty('--sticky-bar-height', `${h}px`);
    }

    /* ---- Variants ---- */

    selection() { return this.selects.map((s) => s.value); }

    find(selection) {
      return this.variants.find((v) => (v.options || []).every((o, i) => o === selection[i]));
    }

    onOptionChange() {
      const selection = this.selection();
      this.apply(this.find(selection), selection);
      this.pushToPage(selection);
    }

    pushToPage(selection) {
      const picker = this.main?.querySelector('[data-variant-picker]');
      if (!picker) return;
      selection.forEach((value, i) => {
        picker.querySelectorAll(`[data-option-input][data-option-position="${i}"]`).forEach((input) => {
          if (input.tagName === 'SELECT') {
            if (input.value === value) return;
            const custom = input.closest('custom-select');
            if (custom?.setValue) custom.setValue(value, { silent: false });
            else { input.value = value; input.dispatchEvent(new Event('change', { bubbles: true })); }
          } else if (input.value === value && !input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    }

    sync(variant) {
      (variant.options || []).forEach((value, i) => {
        const select = this.selects[i];
        if (!select || select.value === value) return;
        const custom = select.closest('custom-select');
        if (custom?.setValue) custom.setValue(value);
        else select.value = value;
      });
      this.apply(this.variants.find((v) => v.id === variant.id) || variant, variant.options || this.selection());
    }

    apply(variant, selection) {
      const t = strings();
      this.markAvailability(selection);

      if (this.idInput) {
        this.idInput.value = variant ? variant.id : '';
        this.idInput.disabled = !variant || !variant.available;
      }

      const wrapper = this.form?.closest('product-form');
      const preorderIds = (wrapper?.dataset.preorderIds || '').split(',').filter(Boolean);
      const isPreorder = !!variant && preorderIds.includes(String(variant.id));
      const preorderInput = this.form?.querySelector('[data-preorder-property]');
      if (preorderInput) preorderInput.disabled = !isPreorder;

      if (this.button) {
        const label = this.button.querySelector('[data-add-label]');
        const addText = isPreorder ? (this.button.dataset.preorderText || this.button.dataset.addText) : (this.button.dataset.addText || t.addToCart);
        this.button.disabled = !variant || !variant.available;
        if (label) label.textContent = !variant ? (t.unavailable || '') : variant.available ? addText : (t.soldOut || '');
      }

      const qty = this.form?.querySelector('[data-quantity-input]');
      const rule = variant?.quantity_rule;
      if (qty && rule) {
        qty.min = rule.min || 1;
        qty.step = rule.increment || 1;
        if (rule.max) qty.max = rule.max; else qty.removeAttribute('max');
        qty.closest('quantity-input')?.commit?.(true);
      }

      if (!variant || variant.id === this.variantId) return;
      this.variantId = variant.id;
      this.renderParts(variant.id);
    }

    markAvailability(selection) {
      const unavailable = strings().unavailable || '';
      this.selects.forEach((select, i) => {
        const check = (value) => this.variants.some((v) => v.available
          && v.options[i] === value
          && v.options.every((o, j) => j === i || !selection[j] || o === selection[j]));
        select.closest('custom-select')?.querySelectorAll('.custom-select__option[data-value]').forEach((li) => {
          const ok = check(li.dataset.value);
          li.dataset.available = String(ok);
          const note = li.querySelector('.custom-select__option-note');
          if (note) { note.textContent = ok ? '' : unavailable; note.hidden = ok; }
        });
      });
    }

    async renderParts(variantId) {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      try {
        const response = await fetch(`${this.productUrl}?variant=${variantId}&section_id=${this.sectionId}${this.view}`, { signal });
        if (!response.ok) throw new Error(response.status);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (signal.aborted) return;
        doc.querySelectorAll('[data-sticky-part][id]').forEach((fresh) => {
          const current = this.querySelector(`#${CSS.escape(fresh.id)}`);
          if (current) current.replaceWith(document.importNode(fresh, true));
        });
      } catch (error) {
        if (error.name !== 'AbortError') console.warn('[sticky-bar]', error);
      }
    }
  }

  customElements.define('product-sticky-bar', ProductStickyBar);
})();
